import { RootState } from "@/store/dtm";
import { employmentType } from "@/types/employmentType";
import { createSlice, PayloadAction } from "@reduxjs/toolkit"
import { addressInfo } from "./PreviousAddressSlice";

export interface employmentState {
    employment: employmentType
}

const initialState: employmentState = {
    employment: {
        id: 0,
        status: "",
        type: "",
        occupation: "",
        occupationIndustry: "",
        jobTitle: "",
        ownershipType: "",
        businessType: "",
        hasBusinessLending: "",
        industryYears: 0,
        industryMonths: 0,
        employmentYears: 0,
        employmentMonths: 0,
        notWorkingYears: 0,
        notWorkingMonths: 0,

        payrollType: "",
        income: 0,
        payrollFrequency: "",
        incomeId: 0,
        hasOvertimeIncome: false,
        overtimeIncome: 0,
        overtimeIncomeFrequency: "",
        overtimeIncomeId: 0,
        hasBonusIncome: false,
        bonusIncome: 0,
        bonusIncomeFrequency: "",
        bonusIncomeId: 0,
        hasCommissionIncome: false,
        commissionIncome: 0,
        commissionIncomeFrequency: "",
        commissionIncomeId: 0,
        hasTipsIncome: false,
        tipsIncome: 0,
        tipsIncomeFrequency: "",
        tipsIncomeId: 0,

        employerName: "",
        employerPhone: "",
        employerAddress: {
            id: 0,
            streetNumber: "",
            streetName: "",
            streetType: "",
            streetDirection: "",
            unitType: "",
            unitNumber: "",
            city: "",
            province: "",
            postalCode: "",
            country: "",
        },

        isProbation: "",
        isFamilyOwned: "",
        isOnLeave: "",
        leaveType: "",
        otherLeaveDescription: "",
        workReturnDate: ""
    }
}


export const employmentSlice = createSlice({
    name: "employment",
    initialState,
    reducers: {
        setEmploymentId: (state, action: PayloadAction<number>) => {
            const id = action.payload;
            state.employment.id = id;
        },
        setEmploymentStatus: (state, action: PayloadAction<string>) => {
            const status = action.payload;
            state.employment.status = status;
        },
        setEmploymentType: (state, action: PayloadAction<string>) => {
            const type = action.payload;
            state.employment.type = type;
        },
        setOccupation: (state, action: PayloadAction<string>) => {
            const occupation = action.payload;
            state.employment.occupation = occupation;
        },
        setJobTitle: (state, action: PayloadAction<string>) => {
            const jobTitle = action.payload;
            state.employment.jobTitle = jobTitle;
        },
        setOwnershipType: (state, action: PayloadAction<string>) => {
            const ownershipType = action.payload;
            state.employment.ownershipType = ownershipType;
        },
        setBusinessType: (state, action: PayloadAction<string>) => {
            const businessType = action.payload;
            state.employment.businessType = businessType;
        },
        setIndustryYears: (state, action: PayloadAction<number>) => {
            const years = action.payload;
            state.employment.industryYears = years;
        },
        setIndustryMonths: (state, action: PayloadAction<number>) => {
            const months = action.payload;
            state.employment.industryMonths = months;
        },
        setEmploymentYears: (state, action: PayloadAction<number>) => {
            const years = action.payload;
            state.employment.employmentYears = years;
        },
        setEmploymentMonths: (state, action: PayloadAction<number>) => {
            const months = action.payload;
            state.employment.employmentMonths = months;
        },
        setBusinessLendingExists: (state, action: PayloadAction<string>) => {
            const exists = action.payload;
            state.employment.hasBusinessLending = exists;
        },

        setPayrollType: (state, action: PayloadAction<string>) => {
            const payrollType = action.payload;
            state.employment.payrollType = payrollType;
        },
        setEmploymentIncome: (state, action: PayloadAction<number>) => {
            const income = action.payload;
            state.employment.income = income;
        },
        setPayrollFrequency: (state, action: PayloadAction<string>) => {
            const payrollFrequency = action.payload;
            state.employment.payrollFrequency = payrollFrequency;
        },

        setOvertimeIncomeExists: (state, action: PayloadAction<boolean>) => {
            const exists = action.payload;
            state.employment.hasOvertimeIncome = exists;
        },
        setOvertimeIncome: (state, action: PayloadAction<number>) => {
            const income = action.payload;
            state.employment.overtimeIncome = income;
        },
        setOvertimeIncomeFrequency: (state, action: PayloadAction<string>) => {
            const frequency = action.payload;
            state.employment.overtimeIncomeFrequency = frequency;
        },

        setBonusIncomeExists: (state, action: PayloadAction<boolean>) => {
            const exists = action.payload;
            state.employment.hasBonusIncome = exists;
        },
        setBonusIncome: (state, action: PayloadAction<number>) => {
            const income = action.payload;
            state.employment.bonusIncome = income;
        },
        setBonusIncomeFrequency: (state, action: PayloadAction<string>) => {
            const frequency = action.payload;
            state.employment.bonusIncomeFrequency = frequency;
        },

        setCommissionIncomeExists: (state, action: PayloadAction<boolean>) => {
            const exists = action.payload;
            state.employment.hasCommissionIncome = exists;
        },
        setCommissionIncome: (state, action: PayloadAction<number>) => {
            const income = action.payload;
            state.employment.commissionIncome = income;
        },
        setCommissionIncomeFrequency: (state, action: PayloadAction<string>) => {
            const frequency = action.payload;
            state.employment.commissionIncomeFrequency = frequency;
        },

        setTipsIncomeExists: (state, action: PayloadAction<boolean>) => {
            const exists = action.payload;
            state.employment.hasTipsIncome = exists;
        },
        setTipsIncome: (state, action: PayloadAction<number>) => {
            const income = action.payload;
            state.employment.tipsIncome = income;
        },
        setTipsIncomeFrequency: (state, action: PayloadAction<string>) => {
            const frequency = action.payload;
            state.employment.tipsIncomeFrequency = frequency;
        },

        setEmployerName: (state, action: PayloadAction<string>) => {
            const name = action.payload;
            state.employment.employerName = name;
        },
        setEmployerPhone: (state, action: PayloadAction<string>) => {
            const phone = action.payload;
            state.employment.employerPhone = phone;
        },
        setEmployerAddress: (state, action: PayloadAction<addressInfo>) => {
            const addrInfo = action.payload;
            state.employment.employerAddress = addrInfo
        },

        setProbationExists: (state, action: PayloadAction<string>) => {
            const exists = action.payload;
            state.employment.isProbation = exists;
        },
        setFamilyOwned: (state, action: PayloadAction<string>) => {
            const familyOwned = action.payload;
            state.employment.isFamilyOwned = familyOwned;
        },
        setOnLeave: (state, action: PayloadAction<string>) => {
            const onLeave = action.payload;
            state.employment.isOnLeave = onLeave;
        },
        setLeaveType: (state, action: PayloadAction<string>) => {
            const leaveType = action.payload;
            state.employment.leaveType = leaveType;
        },
        setOtherLeaveDescription: (state, action: PayloadAction<string>) => {
            const description = action.payload;
            state.employment.otherLeaveDescription = description;
        },
        setWorkReturnDate: (state, action: PayloadAction<string>) => {
            const workReturnDate = action.payload;
            state.employment.workReturnDate = workReturnDate;
        },

        clearEmploymentState: (state) => {
            state.employment.id = 0;
            state.employment.status = "";
            state.employment.type = "";
            state.employment.occupation = "";
            state.employment.jobTitle = "";
            state.employment.hasBusinessLending = "";
            state.employment.industryYears = 0;
            state.employment.industryMonths = 0;
            state.employment.employmentYears = 0;
            state.employment.employmentMonths = 0;

            state.employment.payrollType = "";
            state.employment.income = 0;
            state.employment.payrollFrequency = "";
            state.employment.hasOvertimeIncome = false;
            state.employment.overtimeIncome = 0;
            state.employment.overtimeIncomeFrequency = "";
            state.employment.hasBonusIncome = false;
            state.employment.bonusIncome = 0;
            state.employment.bonusIncomeFrequency = "";
            state.employment.hasCommissionIncome = false;
            state.employment.commissionIncome = 0;
            state.employment.commissionIncomeFrequency = "";
            state.employment.hasTipsIncome = false;
            state.employment.tipsIncome = 0;
            state.employment.tipsIncomeFrequency = "";

            state.employment.employerName = "";
            state.employment.employerPhone = "";
            state.employment.employerAddress = {
                id: 0,
                streetNumber: "",
                streetName: "",
                streetType: "",
                streetDirection: "",
                unitType: "",
                unitNumber: "",
                city: "",
                province: "",
                postalCode: "",
                country: "",
            },

            state.employment.isProbation = "";
            state.employment.isFamilyOwned = "";
            state.employment.isOnLeave = "";
            state.employment.leaveType = "";
            state.employment.workReturnDate = "";

        },
    }
});

export const { setEmploymentId, setEmploymentStatus, setEmploymentType, setPayrollType, setEmploymentIncome, setPayrollFrequency, 
                setOccupation, setJobTitle, setIndustryYears, setIndustryMonths, setEmploymentYears, setEmploymentMonths, setOwnershipType,
                setBusinessType, setBusinessLendingExists, setOvertimeIncomeExists, 
                setOvertimeIncome, setOvertimeIncomeFrequency, setBonusIncomeExists, setBonusIncome, setBonusIncomeFrequency, setCommissionIncomeExists, 
                setCommissionIncome, setCommissionIncomeFrequency, setTipsIncomeExists, setTipsIncome, setTipsIncomeFrequency, setEmployerName, 
                setEmployerPhone, setEmployerAddress, setProbationExists, setFamilyOwned, setOnLeave, setLeaveType, setOtherLeaveDescription, setWorkReturnDate,
                clearEmploymentState } = employmentSlice.actions

export default employmentSlice.reducer

export const selectEmployment = (state: RootState) => state.employment;

